import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { validateApplicant } from "@/components/ApplicantForm";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { PayMethods } from "@/components/PayMethods";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  User, Phone, MapPin, School, Users, HelpCircle, Loader2, ShieldCheck, ArrowLeft, GraduationCap,
} from "lucide-react";

const PROVINCES = ["Gauteng", "Limpopo", "Mpumalanga", "North West", "Free State", "KwaZulu-Natal", "Eastern Cape", "Western Cape", "Northern Cape"];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const YESNO = ["Yes", "No"];

function money(n) {
  return Number(n) === 0 ? "FREE" : `R${Number(n).toFixed(2)}`;
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-slate-700 text-sm">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function Apply() {
  const { university } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const courseIds = (params.get("courses") || "").split(",").filter(Boolean);

  const [uni, setUni] = useState(null);
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({});
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    api.get("/courses/qualifying").then(({ data }) => {
      const all = data.courses || [];
      const picked = all.filter((c) => c.university === university && courseIds.includes(c.id));
      if (!picked.length) {
        toast.error("No courses selected or courses not unlocked. Please choose again.");
        navigate("/dashboard/courses");
        return;
      }
      setCourses(picked);
      setUni({ name: picked[0].university_name, logo: picked[0].university_logo, fee: picked[0].application_fee });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [university]);

  const submit = async () => {
    const missing = validateApplicant(f, { requireGrade: false });
    if (missing.length) {
      toast.error(`Please complete all required fields. Missing: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ` and ${missing.length - 3} more` : ""}.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data: appData } = await api.post("/applications", {
        university,
        course_ids: courseIds,
        applicant: f,
      });
      if (appData.free) {
        toast.success("Application submitted — this university is free!");
        navigate("/dashboard/applications");
        return;
      }
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data: payData } = await api.post("/payments/initialize", {
        type: "application",
        application_id: appData.application.id,
        callback_url,
      });
      window.location.href = payData.authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  if (!uni)
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div>
      </div>
    );

  const isFree = Number(uni.fee) === 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard/courses")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2447] mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to courses
        </button>

        <div className="bg-[#0B2447] text-white rounded-xl p-6 shadow-lg flex items-start gap-5">
          <img src={uni.logo} alt={uni.name} className="w-16 h-16 rounded-lg object-contain bg-white p-1 shrink-0" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-[#F5C32C]">Applying to</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold mt-1">{uni.name}</h1>
            <div className="mt-3 space-y-1.5">
              {courses.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-sm text-white/85" data-testid={`selected-course-${c.id}`}>
                  <GraduationCap className="w-4 h-4 text-[#F5C32C] shrink-0" /> {c.name} <span className="text-white/50">· {c.campus}</span>
                </div>
              ))}
            </div>
            <Badge className={`mt-3 border-0 ${isFree ? "bg-emerald-400 text-emerald-950" : "bg-[#F5C32C] text-[#0B2447]"}`}>
              Application fee: {money(uni.fee)}
            </Badge>
          </div>
        </div>

        <p className="text-slate-600 mt-6">
          Please provide your details for the university application. You can edit any field below.
        </p>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
          <Accordion type="multiple" defaultValue={["personal", "contact"]} className="space-y-3">
            <Section value="personal" icon={User} title="1. Personal Information">
              <Grid>
                <Field label="First Name"><Input data-testid="app-first-name" value={f.first_name || ""} onChange={set("first_name")} /></Field>
                <Field label="Middle Name"><Input value={f.middle_name || ""} onChange={set("middle_name")} /></Field>
                <Field label="Last Name"><Input data-testid="app-last-name" value={f.last_name || ""} onChange={set("last_name")} /></Field>
                <Field label="ID / Passport Number" hint="Used for verification and payment reference">
                  <Input data-testid="app-id-number" value={f.id_number || ""} onChange={set("id_number")} />
                </Field>
                <Field label="Date of Birth"><Input type="date" value={f.dob || ""} onChange={set("dob")} /></Field>
                <Field label="Gender"><SelectBox value={f.gender} onChange={set("gender")} options={GENDERS} placeholder="Select gender" /></Field>
                <Field label="Nationality"><Input value={f.nationality || ""} onChange={set("nationality")} placeholder="Enter your nationality" maxLength={60} /></Field>
                <Field label="Home Language"><Input value={f.home_language || ""} onChange={set("home_language")} /></Field>
              </Grid>
            </Section>

            <Section value="contact" icon={Phone} title="2. Contact Information">
              <Grid>
                <Field label="Email Address *" hint="We'll send application updates here">
                  <Input type="email" data-testid="app-email" value={f.email || ""} onChange={set("email")} />
                </Field>
                <Field label="Phone Number *"><Input data-testid="app-phone" value={f.phone || ""} onChange={set("phone")} /></Field>
                <Field label="WhatsApp Number *"><Input value={f.whatsapp || ""} onChange={set("whatsapp")} /></Field>
              </Grid>
            </Section>

            <Section value="address" icon={MapPin} title="3. Residential Address">
              <Grid>
                <Field label="Street Address" hint="Include house/unit number and street name">
                  <Input value={f.street || ""} onChange={set("street")} />
                </Field>
                <Field label="Suburb / Area"><Input value={f.suburb || ""} onChange={set("suburb")} /></Field>
                <Field label="City / Town"><Input value={f.city || ""} onChange={set("city")} /></Field>
                <Field label="Province"><SelectBox value={f.province} onChange={set("province")} options={PROVINCES} placeholder="Select province" /></Field>
                <Field label="Postal Code"><Input value={f.postal_code || ""} onChange={set("postal_code")} /></Field>
              </Grid>
            </Section>

            <Section value="school" icon={School} title="4. Previous School Attended">
              <Grid>
                <Field label="School Name"><Input value={f.school_name || ""} onChange={set("school_name")} /></Field>
                <Field label="School Province"><SelectBox value={f.school_province} onChange={set("school_province")} options={PROVINCES} placeholder="Select province" /></Field>
                <Field label="Year Completed / Attended"><Input value={f.school_year || ""} onChange={set("school_year")} placeholder="2025" /></Field>
              </Grid>
            </Section>

            <Section value="kin" icon={Users} title="5. Next of Kin / Emergency Contact">
              <Grid>
                <Field label="Full Name"><Input value={f.kin_name || ""} onChange={set("kin_name")} /></Field>
                <Field label="ID Number"><Input value={f.kin_id || ""} onChange={set("kin_id")} /></Field>
                <Field label="Gender"><SelectBox value={f.kin_gender} onChange={set("kin_gender")} options={GENDERS} placeholder="Select gender" /></Field>
                <Field label="Relationship"><Input value={f.kin_relationship || ""} onChange={set("kin_relationship")} /></Field>
                <Field label="Phone Number"><Input value={f.kin_phone || ""} onChange={set("kin_phone")} /></Field>
                <Field label="Email"><Input type="email" value={f.kin_email || ""} onChange={set("kin_email")} /></Field>
              </Grid>
            </Section>

            <Section value="additional" icon={HelpCircle} title="6. Additional Information">
              <Grid>
                <Field label="Are you currently upgrading your marks?"><SelectBox value={f.upgrading} onChange={set("upgrading")} options={YESNO} placeholder="Select" /></Field>
                <Field label="Do you need help with your studies?"><SelectBox value={f.need_help} onChange={set("need_help")} options={YESNO} placeholder="Select" /></Field>
                <Field label="Do you require NSFAS funding?"><SelectBox value={f.nsfas} onChange={set("nsfas")} options={YESNO} placeholder="Select" /></Field>
                <Field label="Are you employed?"><SelectBox value={f.employed} onChange={set("employed")} options={YESNO} placeholder="Select" /></Field>
                <Field label="Have you attended university before?"><SelectBox value={f.attended_before} onChange={set("attended_before")} options={YESNO} placeholder="Select" /></Field>
              </Grid>
            </Section>
          </Accordion>
        </motion.div>

        <div className="sticky bottom-4 mt-8">
          <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-display text-2xl font-bold text-[#0B2447]">{money(uni.fee)}</div>
              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> {isFree ? "This university has no application fee." : "Paid to the university via Paystack."}
              </p>
            </div>
            <Button onClick={submit} disabled={submitting} data-testid="app-submit-pay-btn"
              className="bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 px-8">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isFree ? "Submit Application (Free)" : `Continue to Payment (${money(uni.fee)})`)}
            </Button>
          </div>
          {!isFree && <PayMethods className="mt-3 bg-white border border-slate-200 rounded-xl p-4" />}
          {!isFree && (
            <p className="text-center text-xs text-slate-400 mt-2">
              Payment may take up to 60 seconds to load. Please wait and don't refresh.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ value, icon: Icon, title, children }) {
  return (
    <AccordionItem value={value} className="bg-white border border-slate-200 rounded-lg px-5 shadow-sm">
      <AccordionTrigger className="hover:no-underline">
        <span className="flex items-center gap-3 font-display font-semibold text-[#0B2447]">
          <span className="w-8 h-8 rounded-md bg-[#0B2447]/5 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#0B2447]" />
          </span>
          {title}
        </span>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-5">{children}</AccordionContent>
    </AccordionItem>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}

function SelectBox({ value, onChange, options, placeholder }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="border-slate-300"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
